import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MessageSquare, Download, X, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface LegacyChatCheckResult {
  hasLegacyChats: boolean;
  chatCount: number;
  status: string;
  worldviaUserId: string | null;
  alreadyImportedCount?: number;
}

interface LegacyChatImportResult {
  success: boolean;
  sessionsImported: number;
  messagesImported: number;
  errors: string[];
  duration: number;
}

interface LegacyChatImportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete?: () => void;
  /** If true, force recheck from Bubble even if already imported/declined */
  forceRecheck?: boolean;
}

type ImportState = "checking" | "prompt" | "importing" | "success" | "error" | "declined" | "already_imported" | "no_chats";

export default function LegacyChatImportModal({
  open,
  onOpenChange,
  onImportComplete,
  forceRecheck = false,
}: LegacyChatImportModalProps) {
  const [state, setState] = useState<ImportState>("checking");
  const [checkResult, setCheckResult] = useState<LegacyChatCheckResult | null>(null);
  const [importResult, setImportResult] = useState<LegacyChatImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Track previous open state to detect transitions from closed to open
  const prevOpenRef = useRef(false);
  // Track previous forceRecheck state to detect when user manually requests a recheck
  const prevForceRecheckRef = useRef(false);
  // Track when we're programmatically closing to prevent double onOpenChange invocation
  const isProgrammaticCloseRef = useRef(false);

  // Stable callback to check for legacy chats - takes forceRecheck as parameter
  // to avoid recreating the callback when the prop changes. This is intentionally
  // designed to be stable (empty deps) with forceRecheck passed at call time.
  const checkForLegacyChats = useCallback(async (shouldForceRecheck: boolean) => {
    setState("checking");
    setError(null);

    try {
      // Use recheck endpoint if forcing a fresh check
      const endpoint = shouldForceRecheck 
        ? "/api/legacy-chat-import/recheck"
        : "/api/legacy-chat-import/check";
        
      const response = await fetch(endpoint, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to check for legacy chats");
      }

      const result: LegacyChatCheckResult = await response.json();
      setCheckResult(result);

      if (result.hasLegacyChats) {
        setState("prompt");
      } else if (result.status === "imported" && (result.alreadyImportedCount || 0) > 0) {
        // User already imported their chats
        setState("already_imported");
      } else {
        // No legacy chats available (no_history, no worldviaUserId, or any other state)
        // Always show the "no_chats" state so user gets feedback
        // Note: For auto-check (shouldForceRecheck=false), the useLegacyChatImport hook
        // won't even open the modal if there are no chats, so this branch is mainly
        // hit during manual recheck or edge cases.
        setState("no_chats");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setState("error");
    }
  }, []); // Stable: only uses setState (stable) and parameters

  // Check for legacy chats when modal opens OR when forceRecheck becomes true
  // 
  // Why this pattern:
  // - checkForLegacyChats is stable (empty deps), so including it in deps is safe
  // - We track both `open` and `forceRecheck` transitions to handle two scenarios:
  //   1. Modal opens normally (auto-check or first manual open)
  //   2. Modal is already open and user triggers a manual recheck from user menu
  // - Without tracking forceRecheck transitions, scenario 2 would fail because
  //   the `open && !wasOpen` condition would be false when modal is already open
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    const wasForceRecheck = prevForceRecheckRef.current;
    prevOpenRef.current = open;
    prevForceRecheckRef.current = forceRecheck;
    
    // Run check when:
    // 1. Modal transitions from closed to open, OR
    // 2. Modal is already open and forceRecheck transitions from false to true
    const modalJustOpened = open && !wasOpen;
    const forceRecheckJustEnabled = open && forceRecheck && !wasForceRecheck;
    
    if (modalJustOpened || forceRecheckJustEnabled) {
      checkForLegacyChats(forceRecheck);
    }
  }, [open, forceRecheck, checkForLegacyChats]);

  const handleImport = async () => {
    setState("importing");
    setError(null);

    try {
      const response = await fetch("/api/legacy-chat-import/import", {
        method: "POST",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to import chats");
      }

      const result: LegacyChatImportResult = await response.json();
      setImportResult(result);

      if (result.success) {
        setState("success");
        toast({
          title: "Chat History Imported",
          description: `Successfully imported ${result.sessionsImported} conversations with ${result.messagesImported} messages.`,
        });
        // Note: Don't call onImportComplete here - let the user see the success screen first.
        // onImportComplete will be called when user clicks "Continue to AIVIA" via handleClose.
      } else {
        setError(result.errors.join(", ") || "Import failed");
        setState("error");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setState("error");
    }
  };

  const handleDecline = async () => {
    try {
      await fetch("/api/legacy-chat-import/decline", {
        method: "POST",
        credentials: "include",
      });
      setState("declined");
      isProgrammaticCloseRef.current = true;
      onOpenChange(false);
    } catch (err) {
      // Even if decline fails, close the modal
      isProgrammaticCloseRef.current = true;
      onOpenChange(false);
    }
  };

  // Performs close-related side effects (e.g., notifying parent of import completion)
  // Returns true if closing is allowed, false if blocked (e.g., during import)
  const performCloseActions = useCallback(() => {
    if (state === "importing") {
      return false; // Block close during import
    }
    // If closing after successful import, notify parent to refresh data
    if (state === "success") {
      onImportComplete?.();
    }
    return true;
  }, [state, onImportComplete]);

  // Called by UI buttons to close the modal
  const handleClose = () => {
    if (performCloseActions()) {
      isProgrammaticCloseRef.current = true;
      onOpenChange(false);
    }
  };

  const renderContent = () => {
    switch (state) {
      case "checking":
        return (
          <>
            <DialogTitle className="sr-only">Checking for Legacy Chats</DialogTitle>
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="w-12 h-12 text-orange-600 animate-spin mb-4" />
              <p className="text-gray-600">Checking for your chat history...</p>
            </div>
          </>
        );

      case "prompt":
        return (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-orange-600" />
                Welcome Back to AIVIA!
              </DialogTitle>
              <DialogDescription className="pt-2">
                We found your chat history from the previous version of AIVIA.
              </DialogDescription>
            </DialogHeader>

            <div className="py-6">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="bg-orange-100 rounded-full p-2">
                    <MessageSquare className="w-6 h-6 text-orange-700" />
                  </div>
                  <div>
                    <p className="font-medium text-orange-900">
                      {checkResult?.chatCount || 0} conversations found
                    </p>
                    <p className="text-sm text-orange-700">
                      Import your previous chat history to continue where you left off
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-500">
                Your conversations will be imported into the new AIVIA system. This may take a moment depending on how many messages you have.
              </p>
            </div>

            <DialogFooter className="flex gap-2 sm:gap-2">
              <Button variant="outline" onClick={handleDecline}>
                No, Thanks
              </Button>
              <Button onClick={handleImport} className="bg-orange-600 hover:bg-orange-700">
                <Download className="w-4 h-4 mr-2" />
                Import Chat History
              </Button>
            </DialogFooter>
          </>
        );

      case "importing":
        return (
          <>
            <DialogTitle className="sr-only">Importing Chat History</DialogTitle>
            <div className="flex flex-col items-center justify-center py-12">
              <div className="relative">
                <div className="w-20 h-20 border-4 border-orange-200 rounded-full animate-pulse" />
                <Loader2 className="w-12 h-12 text-orange-600 animate-spin absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
              </div>
              <p className="text-lg font-medium text-gray-900 mt-6">Importing your chat history...</p>
              <p className="text-sm text-gray-500 mt-2">
                This may take a few moments. Please don't close this window.
              </p>
              <div className="flex items-center gap-2 mt-4">
                <div className="w-2 h-2 bg-orange-600 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <div className="w-2 h-2 bg-orange-600 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <div className="w-2 h-2 bg-orange-600 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </>
        );

      case "success":
        return (
          <>
            <DialogTitle className="sr-only">Import Complete</DialogTitle>
            <div className="flex flex-col items-center justify-center py-8">
              <div className="bg-green-100 rounded-full p-3 mb-4">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Import Complete!</h3>
              <p className="text-gray-600 text-center mb-4">
                Successfully imported {importResult?.sessionsImported || 0} conversations
                with {importResult?.messagesImported || 0} messages.
              </p>
              {importResult?.errors && importResult.errors.length > 0 && (
                <p className="text-sm text-amber-600">
                  {importResult.errors.length} items had issues but were skipped.
                </p>
              )}
            </div>

            <DialogFooter>
              <Button onClick={handleClose} className="bg-orange-600 hover:bg-orange-700">
                Continue to AIVIA
              </Button>
            </DialogFooter>
          </>
        );

      case "error":
        return (
          <>
            <DialogTitle className="sr-only">Import Failed</DialogTitle>
            <div className="flex flex-col items-center justify-center py-8">
              <div className="bg-red-100 rounded-full p-3 mb-4">
                <AlertCircle className="w-12 h-12 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Import Failed</h3>
              <p className="text-gray-600 text-center mb-4">
                {error || "Something went wrong while importing your chat history."}
              </p>
            </div>

            <DialogFooter className="flex gap-2 sm:gap-2">
              <Button variant="outline" onClick={handleClose}>
                Close
              </Button>
              <Button onClick={handleImport} className="bg-orange-600 hover:bg-orange-700">
                Try Again
              </Button>
            </DialogFooter>
          </>
        );

      case "already_imported":
        return (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Already Imported
              </DialogTitle>
            </DialogHeader>

            <div className="flex flex-col items-center justify-center py-8">
              <div className="bg-green-100 rounded-full p-3 mb-4">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Chat History Already Imported
              </h3>
              <p className="text-gray-600 text-center mb-4">
                You've already imported {checkResult?.alreadyImportedCount || 0} conversations 
                from your previous AIVIA account.
              </p>
              <p className="text-sm text-gray-500 text-center">
                Your imported chats are available in your threads list.
              </p>
            </div>

            <DialogFooter>
              <Button onClick={handleClose} className="bg-orange-600 hover:bg-orange-700">
                Got It
              </Button>
            </DialogFooter>
          </>
        );

      case "no_chats":
        return (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-gray-600" />
                No Chat History Found
              </DialogTitle>
            </DialogHeader>

            <div className="flex flex-col items-center justify-center py-8">
              <div className="bg-gray-100 rounded-full p-3 mb-4">
                <MessageSquare className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No Legacy Chats Available
              </h3>
              <p className="text-gray-600 text-center mb-4">
                {!checkResult?.worldviaUserId 
                  ? "Your account is not linked to a previous AIVIA account."
                  : "We couldn't find any chat history from your previous AIVIA account."}
              </p>
            </div>

            <DialogFooter>
              <Button onClick={handleClose} variant="outline">
                Close
              </Button>
            </DialogFooter>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onOpenChange={(isOpen) => {
        // Handle close requests from Dialog (clicking outside, pressing ESC)
        // Opening is controlled by parent via the `open` prop, not through this callback
        if (!isOpen) {
          // Check if this close was already handled by handleClose/handleDecline
          // to prevent double invocation of onOpenChange
          if (isProgrammaticCloseRef.current) {
            isProgrammaticCloseRef.current = false;
            return; // Already handled, skip
          }
          // External close trigger (ESC key or click outside)
          if (performCloseActions()) {
            onOpenChange(false);
          }
        }
        // Note: We intentionally don't call onOpenChange(true) for open requests
        // because the parent's onOpenChange is a close-only callback (closeImportModal)
        // that would incorrectly close the modal if called with true
      }}
    >
      <DialogContent 
        className="sm:max-w-md" 
        aria-describedby={undefined}
        onPointerDownOutside={(e) => {
          if (state === "importing") {
            e.preventDefault();
          }
        }}
      >
        {state !== "importing" && state !== "checking" && (
          <button
            onClick={handleClose}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        )}
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
